package guide.kuki.app.engine

import kotlinx.serialization.json.Json

enum class GridMode(val url: String) {
    MIN_100("/files/radial_map_100min.json"),
    MIN_60("/files/radial_map_60min.json"),
}

private const val NAMES_URL = "/files/names.json"

/**
 * Loads and caches [RadialMap] instances from static JSON assets served
 * alongside index.html.
 */
class RadialMapProvider {
    private val cache = mutableMapOf<GridMode, RadialMap>()
    private var names: Map<String, NameEntry> = emptyMap()

    private val json = Json {
        ignoreUnknownKeys = true
        allowTrailingComma = true
    }

    suspend fun loadNames() {
        val bytes = fetchRemoteNamesBytes() ?: platformFetchBytes(NAMES_URL) ?: return
        val text = bytes.decodeToString()
        // A malformed names.json must not leave the app non-functional —
        // fall back to engine-generated defaults (Atai, Etai, …) instead.
        names = try {
            json.decodeFromString(text)
        } catch (_: Throwable) {
            emptyMap()
        }
    }

    suspend fun get(mode: GridMode): RadialMap {
        val cached = cache[mode]
        if (cached != null) return cached

        val bytes = platformFetchBytes(mode.url)
            ?: error("Failed to load radial map from ${mode.url}")
        val text = bytes.decodeToString()
        val data: RadialMapData = json.decodeFromString(text)
        val map = data.toRadialMap()
        if (names.isNotEmpty()) {
            map.loadAddresses(names)
        }
        cache[mode] = map
        return map
    }
}
